import ListingFilterDefinition, {
  ListingFilterDefinitionOption,
  ListingFilterDefinitionOptionBadgeData,
  ListingFilterDefinitionOptionSelectData,
  ListingFilterDefinitionOptionSelectDataType,
} from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerRailyListingFilter, {
  OrderOptimizerRailyListingIgnoreCargoFilter,
  OrderOptimizerRailyListingIgnoreDriverRoutesFilter,
  OrderOptimizerRailyListingIgnoreOnlyWithCargoFilter,
  OrderOptimizerRailyListingIgnoreOnlyWithTaxiFilter,
  OrderOptimizerRailyListingIgnoreTaxiFilter,
  OrderOptimizerRailyListingMaximumDelayFilter,
  OrderOptimizerRailyListingMaximumHaltingTimeFilter,
  OrderOptimizerRailyListingOrderHumanIdFilter,
  OrderOptimizerRailyListingStatusFilter,
  OrderOptimizerRailyListingStatusFilterValue,
} from "../common/types/order-optimizer-raily-listing-filter";
import OrderOptimizerRailyListingFilterType from "../common/types/order-optimizer-raily-listing-filter-type";
import { OrderOptimizerRailyListingItemStatus } from "../common/types/order-optimizer-raily-listing-item";
import orderOptimizerRailyListingTableRowHelper from "../table/row/order-optimizer-raily-listing-table-row.helper";

const orderOptimizerRailyListingFiltersHelper = () => {
  const translations =
    orderOptimizerTranslationsHelper.getRailyListingTranslations();

  const getStatusLabel =
    orderOptimizerRailyListingTableRowHelper.getStatusLabel;

  const createBadgeData = (
    value: string,
    textTemplate: string,
    titleTemplate: string,
  ): ListingFilterDefinitionOptionBadgeData => {
    return {
      text: textTemplate.replace("#{query}", value),
      title: titleTemplate.replace("#{query}", value),
    };
  };

  const getSelectDataForOrderHumanId = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.NUMERIC_QUERY,
      options: [
        {
          label: translations.filters.search.orderHumanIdTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: Number.parseInt(query),
        },
      ],
    };
  };

  const getSelectDataForMaximumDelay = (
    openMaximumDelayModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumDelayTemplateLabel,
        onClick: () => openMaximumDelayModal(),
      },
    };
  };

  const getSelectDataForMaximumHaltingTime = (
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumHaltingTimeTemplateLabel,
        onClick: () => openMaximumHaltingTimeModal(),
      },
    };
  };

  const getSelectDataForIgnoreDriverRoutes =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: [
          {
            label: translations.filters.search.ignoreDriverRoutesTemplateLabel,
            value: true,
          },
        ],
      };
    };

  const getSelectDataForIgnoreTaxi = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.ignoreTaxiTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreOnlyWithTaxi = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label:
            translations.filters.search.ignoreOnlyWithTaxiTemplateLabel.replace(
              "#{query}",
              query,
            ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreCargo = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.ignoreCargoTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreOnlyWithCargo = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label:
            translations.filters.search.ignoreOnlyWithCargoTemplateLabel.replace(
              "#{query}",
              query,
            ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForStatus = (
    status: OrderOptimizerRailyListingItemStatus,
  ): {
    label: string;
    value: OrderOptimizerRailyListingStatusFilterValue;
  }[] => {
    return [
      {
        label: translations.filters.search.statusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: false,
        },
      },
      {
        label: translations.filters.search.excludeStatusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: true,
        },
      },
    ];
  };

  const getSelectDataForStatuses =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: Object.values(OrderOptimizerRailyListingItemStatus)
          .map(getSelectDataForStatus)
          .flat(),
      };
    };

  const getSelectDataByFilterType = (
    type: OrderOptimizerRailyListingFilterType,
    query: string,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    const options: Record<
      OrderOptimizerRailyListingFilterType,
      (query: string) => ListingFilterDefinitionOptionSelectData
    > = {
      [OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID]:
        getSelectDataForOrderHumanId,
      [OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY]: () =>
        getSelectDataForMaximumDelay(openMaximumDelayModal),
      [OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME]: () =>
        getSelectDataForMaximumHaltingTime(openMaximumHaltingTimeModal),
      [OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES]:
        getSelectDataForIgnoreDriverRoutes,
      [OrderOptimizerRailyListingFilterType.IGNORE_TAXI]:
        getSelectDataForIgnoreTaxi,
      [OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_TAXI]:
        getSelectDataForIgnoreOnlyWithTaxi,
      [OrderOptimizerRailyListingFilterType.IGNORE_CARGO]:
        getSelectDataForIgnoreCargo,
      [OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_CARGO]:
        getSelectDataForIgnoreOnlyWithCargo,
      [OrderOptimizerRailyListingFilterType.STATUS]: getSelectDataForStatuses,
    };

    return options[type](query);
  };

  const getBadgeForOrderHumanId = (
    filterValue: OrderOptimizerRailyListingOrderHumanIdFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.orderHumanIdLabel,
      translations.filters.orderHumanIdTitle,
    );
  };

  const getBadgeForMaximumDelay = (
    filterValue: OrderOptimizerRailyListingMaximumDelayFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumDelayLabel,
      translations.filters.maximumDelayTitle,
    );
  };

  const getBadgeForMaximumHaltingTime = (
    filterValue: OrderOptimizerRailyListingMaximumHaltingTimeFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumHaltingTimeLabel,
      translations.filters.maximumHaltingTimeTitle,
    );
  };

  const getBadgeForIgnoreDriverRoutes = (
    filterValue: OrderOptimizerRailyListingIgnoreDriverRoutesFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.ignoreDriverRoutesLabel,
      translations.filters.ignoreDriverRoutesTitle,
    );
  };

  const getBadgeForIgnoreTaxi = (
    filterValue: OrderOptimizerRailyListingIgnoreTaxiFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreTaxiLabel,
      translations.filters.ignoreTaxiTitle,
    );
  };

  const getBadgeForIgnoreOnlyWithTaxi = (
    filterValue: OrderOptimizerRailyListingIgnoreOnlyWithTaxiFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreOnlyWithTaxiLabel,
      translations.filters.ignoreOnlyWithTaxiTitle,
    );
  };

  const getBadgeForIgnoreCargo = (
    filterValue: OrderOptimizerRailyListingIgnoreCargoFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreCargoLabel,
      translations.filters.ignoreCargoTitle,
    );
  };

  const getBadgeForIgnoreOnlyWithCargo = (
    filterValue: OrderOptimizerRailyListingIgnoreOnlyWithCargoFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreOnlyWithCargoLabel,
      translations.filters.ignoreOnlyWithCargoTitle,
    );
  };

  const getBadgeDataForStatus = (
    filterValue: OrderOptimizerRailyListingStatusFilter["value"],
  ) => {
    if (filterValue.exclude) {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.excludeStatusLabel,
        translations.filters.excludeStatusTtitle,
      );
    } else {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.statusLabel,
        translations.filters.statusTitle,
      );
    }
  };

  const getBadgeDataByFilterType = (
    type: OrderOptimizerRailyListingFilterType,
    filterValue: OrderOptimizerRailyListingFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    const options: Record<
      OrderOptimizerRailyListingFilterType,
      (
        filterValue: OrderOptimizerRailyListingFilter["value"],
      ) => ListingFilterDefinitionOptionBadgeData
    > = {
      [OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID]:
        getBadgeForOrderHumanId,
      [OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY]:
        getBadgeForMaximumDelay,
      [OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME]:
        getBadgeForMaximumHaltingTime,
      [OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES]:
        getBadgeForIgnoreDriverRoutes,
      [OrderOptimizerRailyListingFilterType.IGNORE_TAXI]: getBadgeForIgnoreTaxi,
      [OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_TAXI]:
        getBadgeForIgnoreOnlyWithTaxi,
      [OrderOptimizerRailyListingFilterType.IGNORE_CARGO]:
        getBadgeForIgnoreCargo,
      [OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_CARGO]:
        getBadgeForIgnoreOnlyWithCargo,
      [OrderOptimizerRailyListingFilterType.STATUS]: getBadgeDataForStatus,
    };

    return options[type](filterValue);
  };

  const getMultiApplicableFlagByType = (
    type: OrderOptimizerRailyListingFilterType,
  ): boolean => {
    const options: Record<OrderOptimizerRailyListingFilterType, boolean> = {
      IGNORE_CARGO: true,
      IGNORE_DRIVER_ROUTES: false,
      IGNORE_ONLY_WITH_CARGO: true,
      IGNORE_ONLY_WITH_TAXI: true,
      IGNORE_TAXI: true,
      MAXIMUM_DELAY: false,
      MAXIMUM_HALTING_TIME: false,
      ORDER_HUMAN_ID: false,
      STATUS: false,
    };

    return options[type];
  };

  const getFilterDefinitionOptionByType = (
    type: OrderOptimizerRailyListingFilterType,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderOptimizerRailyListingFilter> => {
    return {
      filterType: type,
      isMultiApplicable: getMultiApplicableFlagByType(type),
      getSelectData: (query) =>
        getSelectDataByFilterType(
          type,
          query,
          openMaximumDelayModal,
          openMaximumHaltingTimeModal,
        ),
      getBadgeData: (value) => getBadgeDataByFilterType(type, value),
    };
  };

  const getFilterDefinitionOptions = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderOptimizerRailyListingFilter>[] => {
    return Object.values(OrderOptimizerRailyListingFilterType).map((type) =>
      getFilterDefinitionOptionByType(
        type,
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    );
  };

  const getFilterDefinition = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinition<OrderOptimizerRailyListingFilter> => {
    return {
      options: getFilterDefinitionOptions(
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    };
  };

  const STATUS_FILTER_EXCLUSION_PREFIX = "not-";

  const getStatusRouteQueryParamFromValue = (
    value: OrderOptimizerRailyListingStatusFilterValue,
  ): string => {
    return `${value.exclude ? STATUS_FILTER_EXCLUSION_PREFIX : ""}${value.status.toLocaleLowerCase()}`;
  };

  const getStatusValueFromRouteQueryParam = (
    routeQueryParam: string,
  ): OrderOptimizerRailyListingStatusFilterValue | undefined => {
    const checkRouteParam = (param: string): boolean => {
      return Object.values(OrderOptimizerRailyListingItemStatus)
        .map((status) => status.toLocaleLowerCase())
        .includes(param);
    };

    if (routeQueryParam.startsWith(STATUS_FILTER_EXCLUSION_PREFIX)) {
      const splitParam = routeQueryParam.split("-");
      if (splitParam.length !== 2) return;
      if (!checkRouteParam(splitParam[1])) return;

      return {
        exclude: true,
        status:
          splitParam[1].toLocaleUpperCase() as OrderOptimizerRailyListingItemStatus,
      };
    } else {
      if (!checkRouteParam(routeQueryParam)) return;

      return {
        exclude: false,
        status:
          routeQueryParam.toLocaleUpperCase() as OrderOptimizerRailyListingItemStatus,
      };
    }
  };

  return {
    getFilterDefinition,
    getStatusRouteQueryParamFromValue,
    getStatusValueFromRouteQueryParam,
  };
};

export default orderOptimizerRailyListingFiltersHelper;
